import React, { FC, createContext, useMemo, useState, PropsWithChildren, useContext, useCallback } from 'react'
import { ConfigSchema } from '../types/gotypes'
import { configApi } from '../services/configApi'

export interface IConfigSchemaContext {
  schema: ConfigSchema | null
  loading: boolean
  error: Error | null
  loadSchema: () => Promise<void>
}

export const ConfigSchemaContext = createContext<IConfigSchemaContext>({
  schema: null,
  loading: false,
  error: null,
  loadSchema: async () => {},
})

// The schema in sessionStorage may come from before a release. It may draw the
// first render, but each page load fetches the schema once, so fields added by
// a release appear without a new tab (Stats review, #1653, 11 Oct).
let fetchedThisPageLoad = false

export const useConfigSchemaContext = (): IConfigSchemaContext => {
  const [schema, setSchema] = useState<ConfigSchema | null>(() => {
    // Try to load from session storage on initial mount
    const cached = sessionStorage.getItem('configSchema')
    if (cached) {
      try {
        return JSON.parse(cached)
      } catch {
        return null
      }
    }
    return null
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadSchema = useCallback(async () => {
    // Fetched in this page load already: the schema is current.
    if (fetchedThisPageLoad) return
    fetchedThisPageLoad = true

    setLoading(true)
    setError(null)
    try {
      const data = await configApi.getSchema()
      setSchema(data)
      sessionStorage.setItem('configSchema', JSON.stringify(data))
    } catch (err) {
      fetchedThisPageLoad = false
      setError(err instanceof Error ? err : new Error('Failed to load config schema'))
    } finally {
      setLoading(false)
    }
  }, [])

  const contextValue = useMemo<IConfigSchemaContext>(() => ({
    schema,
    loading,
    error,
    loadSchema,
  }), [schema, loading, error, loadSchema])

  return contextValue
}

export const ConfigSchemaProvider: FC<PropsWithChildren> = ({ children }) => {
  const value = useConfigSchemaContext()

  return (
    <ConfigSchemaContext.Provider value={value}>
      {children}
    </ConfigSchemaContext.Provider>
  )
}

/**
 * Hook to access the config schema context
 * Use this in components that need access to config field definitions
 */
export const useConfigSchema = () => {
  const context = useContext(ConfigSchemaContext)
  return context
}
